import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@radiology/api-client';
import type { ModuleKey } from './api';

/**
 * Role scopes and lookup vocabularies.
 *
 * Both exist because of what the legacy certifications admin form actually did,
 * which Phase 4 had modelled wrongly:
 *
 *  - It gave Supervisor and Manager a hospital+section **per assignment**, and
 *    allowed more than one assignment per role. That list migrated into
 *    `user_profiles.user_context` and nothing read it, so four real supervisors
 *    have been scoped to the wrong section. `core.role_scopes` holds it now.
 *  - Hospital, section and title were **dropdowns** fed from the attributes
 *    table, not free text. Only department was typed by hand, which is how the
 *    migrated `department_id` values ended up inconsistent.
 */

export interface RoleScope {
  id: string;
  user_id: string;
  module: ModuleKey;
  role: string;
  hospital: string;
  /** NULL means the whole hospital — how a manager is scoped. */
  section: string | null;
}

const SCOPES = ['admin', 'role_scopes'] as const;

export function useRoleScopes() {
  return useQuery({
    queryKey: SCOPES,
    queryFn: async (): Promise<RoleScope[]> => {
      const { data, error } = await supabase()
        .from('role_scopes')
        .select('*')
        .returns<RoleScope[]>();
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

/**
 * Add assignments for one role.
 *
 * Takes LISTS of hospitals and sections and writes the cross product, because
 * that is what the manager CR specifies: AC-04 and AC-05 ask for multi-select on
 * both, and the worked example — Hospitals [A, B] x Sections [Cardiology,
 * Emergency] — means all four combinations, not two pairs.
 *
 * Storing pairs rather than two lists is deliberate and strictly more
 * expressive: it can also say "Hospital A / Cardiology only", which two
 * independent lists cannot.
 */
export function useAddScope() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      userId: string;
      module: ModuleKey;
      role: string;
      hospitals: string[];
      sections: string[];
    }) => {
      const sections = input.sections.length > 0 ? input.sections : [null];
      const rows = input.hospitals.flatMap(hospital =>
        sections.map(section => ({
          user_id: input.userId,
          module: input.module,
          role: input.role,
          hospital,
          section,
        })),
      );
      if (rows.length === 0) return;

      // Re-adding a combination that already exists must not fail the whole
      // batch — the unique index is on (user, module, role, hospital, section).
      const { error } = await supabase().from('role_scopes').upsert(rows, {
        onConflict: 'user_id,module,role,hospital,section',
        ignoreDuplicates: true,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: SCOPES }),
  });
}

export function useRemoveScope() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase().from('role_scopes').delete().eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: SCOPES }),
  });
}

/**
 * A lookup vocabulary, across all modules.
 *
 * A profile is global but `core.attributes` is per-module, so an administrator
 * setting somebody's hospital needs the union. `core.lookup_options()` is
 * SECURITY DEFINER with an administrator guard for exactly that reason — the
 * ordinary attributes policy shows a reader only their own modules.
 */
export function useLookup(key: string) {
  return useQuery({
    queryKey: ['admin', 'lookup', key],
    queryFn: async (): Promise<string[]> => {
      const result = (await supabase().rpc('lookup_options', { p_key: key })) as unknown as {
        data: { value: string }[] | null;
        error: { message: string } | null;
      };
      if (result.error) throw new Error(result.error.message);
      return (result.data ?? []).map(r => r.value);
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Several vocabularies merged into one list.
 *
 * Department is spelled `department` in purchasing and `department_id` in
 * devices — the same idea under two keys, because each legacy app named it
 * differently. An administrator should see one list.
 */
export function useLookupUnion(keys: string[]) {
  const results = useQueries({
    queries: keys.map(key => ({
      queryKey: ['admin', 'lookup', key],
      queryFn: async (): Promise<string[]> => {
        const result = (await supabase().rpc('lookup_options', { p_key: key })) as unknown as {
          data: { value: string }[] | null;
          error: { message: string } | null;
        };
        if (result.error) throw new Error(result.error.message);
        return (result.data ?? []).map(r => r.value);
      },
      staleTime: 5 * 60 * 1000,
    })),
  });

  const values = [...new Set(results.flatMap(r => r.data ?? []))].sort((a, b) =>
    a.localeCompare(b),
  );
  return { data: values, isLoading: results.some(r => r.isLoading) };
}

/**
 * Which roles carry their own hospital/section.
 *
 * Straight from the legacy form: it created a context entry for Supervisor and
 * Manager only. An employee is scoped by their own certificates and an admin is
 * unscoped, so neither has anything to assign.
 */
export const SCOPED_ROLES: Record<ModuleKey, string[]> = {
  // `operation-manager` is deliberately absent: BR-03 gives it no restrictions,
  // so an assignment would be a control that does nothing.
  certifications: ['supervisor', 'manager'],
  purchasing: [],
  attendance: [],
  devices: [],
};

export function roleIsScoped(module: ModuleKey, role: string): boolean {
  return SCOPED_ROLES[module]?.includes(role) ?? false;
}

/**
 * Every scoped certifications role now needs a section.
 *
 * This changed with the manager CR. Before it, `manager` meant hospital-wide and
 * its scope section was NULL. BR-04 and BR-08 make the new Manager
 * hospital+section, and BR-07 makes both mandatory — so there is no longer a
 * role whose assignment is a hospital alone.
 */
export function scopeNeedsSection(_role: string): boolean {
  return true;
}
